import React from 'react';
import { Download, Edit, Link2, Loader2, Maximize, Minimize, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface BrandProfileHeaderProps {
  brandName: string;
  slug: string;
  isExpanded: boolean;
  isExporting: boolean;
  onFullscreenClick: () => void;
  onExportPDF: () => void;
  onExportImage: () => void;
  onCopyLink: () => void;
  onEdit: () => void;
}

export function BrandProfileHeader({
  brandName,
  slug,
  isExpanded,
  isExporting,
  onFullscreenClick,
  onExportPDF,
  onExportImage,
  onCopyLink,
  onEdit,
}: BrandProfileHeaderProps) {
  return (
    <TooltipProvider>
      <div className={`flex items-center justify-between border-b border-border ${isExpanded ? 'p-4' : 'px-3 py-2'}`}>
        {/* Left: Brand name */}
        <div>
          <h2 className={`font-bold text-foreground ${isExpanded ? 'text-xl' : 'text-lg'}`}>
            {brandName}
          </h2>
          {isExpanded && (
            <p className="text-sm text-muted-foreground font-mono">
              brand://profile/{slug}
            </p>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Export dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size={isExpanded ? 'sm' : 'icon'} disabled={isExporting}>
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isExpanded && <span className="ml-2">Exporting...</span>}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    {isExpanded && <span className="ml-2">Export</span>}
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onExportPDF} disabled={isExporting}>
                <Download className="h-4 w-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportImage} disabled={isExporting}>
                <Download className="h-4 w-4 mr-2" />
                Export as Image
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onCopyLink}>
                <Link2 className="h-4 w-4 mr-2" />
                Copy Brand Link
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Edit button */}
          {isExpanded && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit brand profile</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* Fullscreen toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size={isExpanded ? 'sm' : 'icon'}
                onClick={onFullscreenClick}
              >
                {isExpanded ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isExpanded ? 'Exit fullscreen' : 'Enter fullscreen'}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
